import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { MetaDefault, SegmentGroupHorizontal } from '@terra/ui-design-system';
import { HStack, VStack } from '@terra/ui-styled-system';
import type { UserTenant } from '@terra/shared-contracts-gateway-auth';
import type { PortalTarget } from '../../../store/projectStore';
import type { WorkspaceSummary } from '../../../electron-shared/workspace/workspace-descriptor';
import { TargetSelectorSearchInput } from '../../layout/target-selector/TargetSelectorSearchInput';
import { LauncherTargetList } from './LauncherTargetList';
import { LauncherTenantSelect } from './LauncherTenantSelect';
import {
  ALL_SURFACES_FILTER,
  type LauncherSurfaceFilter,
} from './launcher-targets';
import { useLauncherTargets } from './useLauncherTargets';

type SurfaceFilterOption = {
  value: LauncherSurfaceFilter;
  label: string;
  disabled?: boolean;
};

type ChooseTargetSectionProps = {
  tenants: UserTenant[];
  currentTenantId: string | null;
  tenantName: string;
  recents: readonly WorkspaceSummary[];
  openTargetIds: ReadonlySet<string>;
  onOpen: (target: PortalTarget) => void;
};

export function ChooseTargetSection({
  tenants,
  currentTenantId,
  tenantName,
  recents,
  openTargetIds,
  onOpen,
}: ChooseTargetSectionProps) {
  const t = useTranslations('proxy.workspaces');
  const [query, setQuery] = useState('');
  const [surfaceFilter, setSurfaceFilter] =
    useState<LauncherSurfaceFilter>(ALL_SURFACES_FILTER);
  const [isTenantSelectOpen, setIsTenantSelectOpen] = useState(false);
  const { status, surfaceGroups, items } = useLauncherTargets({
    query,
    surfaceFilter,
    recents,
    tenantId: currentTenantId,
  });
  const canSwitchTenant = tenants.length > 1;
  const surfaceOptions: SurfaceFilterOption[] = [
    { value: ALL_SURFACES_FILTER, label: t('allTargets') },
    ...surfaceGroups.map(({ surface, label, targets }) => ({
      value: surface.targetType,
      label,
      disabled: targets.length === 0,
    })),
  ];

  return (
    <VStack gap="3" w="full" alignItems="stretch">
      <HStack gap="2" alignItems="center">
        <MetaDefault color="fg.neutral.muted">
          {canSwitchTenant ? t('chooseTargetIn') : t('chooseTarget')}
        </MetaDefault>
        {canSwitchTenant && (
          <LauncherTenantSelect
            tenantName={tenantName}
            isOpen={isTenantSelectOpen}
            onOpenChange={setIsTenantSelectOpen}
          />
        )}
      </HStack>
      <TargetSelectorSearchInput
        value={query}
        onChange={setQuery}
        placeholder={
          canSwitchTenant
            ? t('searchTargetsIn', { tenantName })
            : t('searchTargets')
        }
        clearLabel={t('clearSearch')}
      />
      <SegmentGroupHorizontal
        options={surfaceOptions}
        value={surfaceFilter}
        onValueChange={({ value }) => {
          const nextFilter = surfaceOptions.find(
            (option) => option.value === value,
          )?.value;

          if (nextFilter) {
            setSurfaceFilter(nextFilter);
          }
        }}
      />
      <LauncherTargetList
        status={status}
        items={items}
        query={query}
        tenantName={canSwitchTenant ? tenantName : undefined}
        openTargetIds={openTargetIds}
        onOpen={onOpen}
        onSearchAnotherTenant={
          canSwitchTenant ? () => setIsTenantSelectOpen(true) : undefined
        }
      />
    </VStack>
  );
}
